import { forwardRef } from 'react';
import AORLogo from './AORLogo';

const fmtP = n => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(n);
const BLUE='#2563EB', DARK='#0F172A', GREEN='#16A34A', RED='#DC2626', AMBER='#D97706', GOLD='#B8963E';

const COVERAGE_LABELS = {
  auto:'Commercial Auto', gl:'General Liability', wc:"Workers' Compensation",
  prop:'Commercial Property', property:'Commercial Property',
  umbrella:'Umbrella / Excess Liability', inland:'Inland Marine / Tools',
  eo:'E&O / Professional Liability', flood:'Flood Insurance',
};

const PAGE = {
  width:'100%', boxSizing:'border-box', padding:'18mm 20mm 22mm',
  position:'relative', background:'#fff', pageBreakAfter:'always',
  minHeight:'277mm',
};

function PHeader({title, subtitle}) {
  return(
    <div style={{borderBottom:`3px solid ${BLUE}`,paddingBottom:10,marginBottom:18}}>
      <div style={{fontSize:8,color:BLUE,fontFamily:'sans-serif',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:3}}>AOR Insurance — Confidential Analysis</div>
      <div style={{fontSize:20,fontWeight:900,color:DARK,fontFamily:'sans-serif',letterSpacing:'-0.02em'}}>{title}</div>
      {subtitle&&<div style={{fontSize:10,color:'#64748b',fontFamily:'sans-serif',marginTop:2}}>{subtitle}</div>}
    </div>
  );
}

function PFooter({page, company}) {
  return(
    <div style={{position:'absolute',bottom:8,left:'20mm',right:'20mm',borderTop:'1px solid #E2E8F0',paddingTop:5,display:'flex',justifyContent:'space-between',fontSize:8,color:'#94a3b8',fontFamily:'sans-serif'}}>
      <span>AOR Insurance — Confidential — Prepared for {company}</span>
      <span>Page {page}</span>
    </div>
  );
}

function GapBlock({gaps, color, label}) {
  if(!gaps.length) return null;
  return(
    <div style={{marginBottom:14}}>
      <div style={{background:color,padding:'5px 10px',borderRadius:'6px 6px 0 0',fontSize:9,fontWeight:800,color:'#fff',fontFamily:'sans-serif',textTransform:'uppercase',letterSpacing:'0.08em'}}>
        {label} — {gaps.length}
      </div>
      <div style={{border:`1px solid ${color}30`,borderTop:'none',borderRadius:'0 0 6px 6px',padding:'8px 10px'}}>
        {gaps.map((g,i)=>(
          <div key={i} style={{paddingBottom:i<gaps.length-1?8:0,marginBottom:i<gaps.length-1?8:0,borderBottom:i<gaps.length-1?`1px solid ${color}18`:'none'}}>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:2}}>
              <div style={{width:3,height:'100%',background:color,alignSelf:'stretch',flexShrink:0,borderRadius:2}}/>
              <span style={{fontSize:10,fontWeight:800,color:DARK,fontFamily:'sans-serif'}}>{g.title}</span>
              <span style={{fontSize:7,fontWeight:700,color,background:`${color}15`,padding:'1px 5px',borderRadius:99,fontFamily:'sans-serif',border:`1px solid ${color}30`,flexShrink:0,letterSpacing:'0.06em'}}>{label.split(' ')[0]}</span>
            </div>
            <div style={{fontSize:9,color:'#475569',fontFamily:'sans-serif',lineHeight:1.55,paddingLeft:8}}>{g.desc}</div>
            {g.exposureCost&&(
              <div style={{marginTop:3,paddingLeft:8,fontSize:8,fontWeight:700,color:RED,fontFamily:'sans-serif'}}>
                Annual exposure cost (1-in-5 probability): {fmtP(g.exposureCost)}/yr
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const PrintReport = forwardRef(function PrintReport({data, results, savings, totalAORValue, exposureTotal}, ref) {
  if(!results) return null;

  const today = new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'});
  const company = data.companyName || 'Your Company';
  const totalCur = Object.values(results.cur).reduce((a,b)=>a+b,0);
  const savPct = totalCur ? Math.round((savings/totalCur)*100) : 0;

  const criticalGaps = results.gaps.filter(g=>g.level==='red');
  const gapList      = results.gaps.filter(g=>g.level==='amber');
  const optList      = results.gaps.filter(g=>g.level==='blue');

  const rows = Object.entries(results.cur)
    .filter(([,v])=>v>0)
    .map(([k,v])=>({
      name: COVERAGE_LABELS[k]||k,
      cur: v, opt: results.opt[k]||0,
      savings: v-(results.opt[k]||0),
      pct: Math.round(((v-(results.opt[k]||0))/v)*100),
      entered: !!(results.userEntered?.[k]),
    }));

  return(
    <div ref={ref} id="print-report" style={{background:'#fff',fontFamily:'sans-serif'}}>

      {/* ── PAGE 1: COVER ── */}
      <div className="print-page" style={{...PAGE,display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',textAlign:'center'}}>
        <AORLogo height={56} style={{marginBottom:32}}/>
        <div style={{fontSize:9,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.18em',marginBottom:14}}>Confidential Risk &amp; Coverage Analysis</div>
        <div style={{fontSize:38,fontWeight:900,color:DARK,letterSpacing:'-0.03em',lineHeight:1.1,marginBottom:8}}>{company}</div>
        <div style={{fontSize:13,color:'#64748b',marginBottom:36}}>{[data.primaryState, data.industry].filter(Boolean).join(' · ')}</div>
        <div style={{width:72,height:3,background:`linear-gradient(90deg,${BLUE},${GOLD})`,borderRadius:99,marginBottom:36}}/>
        <div style={{display:'flex',gap:36,justifyContent:'center'}}>
          {[
            {lb:'Prepared by',vl:'AOR Insurance'},
            {lb:'Date',vl:today},
            {lb:'Prepared for',vl:data.contactName||company},
          ].map((item,i)=>(
            <div key={i} style={{textAlign:'center'}}>
              <div style={{fontSize:8,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:3}}>{item.lb}</div>
              <div style={{fontSize:12,fontWeight:700,color:DARK}}>{item.vl}</div>
            </div>
          ))}
        </div>
        <div style={{position:'absolute',bottom:16,fontSize:8,color:'#CBD5E1'}}>CONFIDENTIAL — FOR AUTHORIZED RECIPIENT ONLY</div>
      </div>

      {/* ── PAGE 2: EXECUTIVE SUMMARY ── */}
      <div className="print-page" style={PAGE}>
        <PHeader title="Executive Summary" subtitle={`Risk & Coverage Analysis — ${company} — ${today}`}/>

        {/* 3 key numbers */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:18}}>
          {[
            {lb:'Current Estimated Premiums',vl:fmtP(totalCur),cl:RED,bg:'#FEF2F2',border:'#FECACA'},
            {lb:'AOR Optimized Target',vl:fmtP(totalCur-savings),cl:GREEN,bg:'#F0FDF4',border:'#BBF7D0'},
            {lb:'Total AOR Value',vl:fmtP(totalAORValue),cl:GOLD,bg:'#FEFCE8',border:'#FDE68A'},
          ].map((c,i)=>(
            <div key={i} style={{background:c.bg,borderRadius:10,padding:'14px 16px',border:`1.5px solid ${c.border}`,textAlign:'center'}}>
              <div style={{fontSize:8,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>{c.lb}</div>
              <div style={{fontSize:26,fontWeight:900,color:c.cl,letterSpacing:'-0.02em'}}>{c.vl}</div>
            </div>
          ))}
        </div>

        {/* Savings breakdown */}
        <div style={{background:'#F8FAFC',borderRadius:10,padding:'12px 16px',border:'1px solid #E2E8F0',marginBottom:16}}>
          <div style={{fontSize:9,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Value Breakdown</div>
          {[
            {lb:'Premium Optimization',vl:fmtP(savings)+'/yr'},
            ...(exposureTotal>0?[{lb:'Uninsured Exposure Eliminated (annual equiv.)',vl:fmtP(exposureTotal)+'/yr'}]:[]),
          ].map((r,i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
              <span style={{fontSize:10,color:'#475569'}}>{r.lb}</span>
              <span style={{fontSize:11,fontWeight:700,color:GREEN}}>{r.vl}</span>
            </div>
          ))}
          <div style={{borderTop:'1px solid #E2E8F0',paddingTop:7,marginTop:7,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:11,fontWeight:800,color:DARK}}>Total AOR Value</span>
            <span style={{fontSize:14,fontWeight:900,color:GOLD}}>{fmtP(totalAORValue)}/yr</span>
          </div>
        </div>

        {/* Profile + Risk score */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
          <div style={{background:'#F8FAFC',borderRadius:10,padding:'12px 14px',border:'1px solid #E2E8F0'}}>
            <div style={{fontSize:9,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Risk Score</div>
            <div style={{fontSize:34,fontWeight:900,color:results.riskScore>=60?RED:results.riskScore>=35?AMBER:GREEN,letterSpacing:'-0.02em',lineHeight:1}}>{results.riskScore}/100</div>
            <div style={{fontSize:9,color:'#94a3b8',marginTop:4}}>
              {results.riskScore>=60?'High Risk — Immediate Review Recommended':results.riskScore>=35?'Moderate Risk — Optimization Recommended':'Lower Risk Profile — Optimization Available'}
            </div>
          </div>
          <div style={{background:'#F8FAFC',borderRadius:10,padding:'12px 14px',border:'1px solid #E2E8F0'}}>
            <div style={{fontSize:9,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Business Profile</div>
            {[
              ['State',data.primaryState||'—'],
              ['Employees',data.employees||'—'],
              ['Annual Revenue',data.revenue?fmtP(parseInt(data.revenue)):'—'],
              ['Fleet Size',data.fleetSize||'0'],
              ['Claims (3yr)',data.claims||'0'],
            ].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                <span style={{fontSize:9,color:'#94a3b8'}}>{l}</span>
                <span style={{fontSize:9,fontWeight:600,color:DARK}}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gap counts */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
          {[
            {lb:'Critical Gaps',vl:criticalGaps.length,cl:RED,bg:'#FEF2F2'},
            {lb:'Coverage Gaps',vl:gapList.length,cl:AMBER,bg:'#FFFBEB'},
            {lb:'Optimizations',vl:optList.length,cl:BLUE,bg:'#EFF6FF'},
          ].map((c,i)=>(
            <div key={i} style={{background:c.bg,borderRadius:8,padding:'10px 14px',textAlign:'center'}}>
              <div style={{fontSize:28,fontWeight:900,color:c.cl}}>{c.vl}</div>
              <div style={{fontSize:8,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.08em'}}>{c.lb}</div>
            </div>
          ))}
        </div>

        <PFooter page={2} company={company}/>
      </div>

      {/* ── PAGE 3: COVERAGE ANALYSIS ── */}
      <div className="print-page" style={PAGE}>
        <PHeader title="Coverage Analysis" subtitle="Current premiums vs. AOR optimized targets by line of coverage"/>

        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:16}}>
          <thead>
            <tr style={{background:DARK}}>
              {['Coverage Line','Current Premium','AOR Target','Savings','Reduction','Source'].map((h,i)=>(
                <th key={i} style={{padding:'8px 10px',fontSize:8,color:'rgba(255,255,255,0.8)',textTransform:'uppercase',letterSpacing:'0.08em',textAlign:i===0?'left':'right',fontWeight:700,fontFamily:'sans-serif'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row,i)=>(
              <tr key={i} style={{background:i%2===0?'#F8FAFC':'#fff',borderBottom:'1px solid #E2E8F0'}}>
                <td style={{padding:'9px 10px',fontSize:11,fontWeight:600,color:DARK,fontFamily:'sans-serif'}}>{row.name}</td>
                <td style={{padding:'9px 10px',fontSize:11,color:RED,fontFamily:'sans-serif',textAlign:'right',fontWeight:600}}>{fmtP(row.cur)}</td>
                <td style={{padding:'9px 10px',fontSize:11,color:GREEN,fontFamily:'sans-serif',textAlign:'right',fontWeight:600}}>{fmtP(row.opt)}</td>
                <td style={{padding:'9px 10px',fontSize:12,color:GREEN,fontFamily:'sans-serif',textAlign:'right',fontWeight:700}}>{fmtP(row.savings)}</td>
                <td style={{padding:'9px 10px',fontSize:11,color:GREEN,fontFamily:'sans-serif',textAlign:'right'}}>{row.pct}%</td>
                <td style={{padding:'9px 10px',fontSize:8,color:'#94a3b8',fontFamily:'sans-serif',textAlign:'right'}}>{row.entered?'Entered':'Estimated'}</td>
              </tr>
            ))}
            <tr style={{background:DARK}}>
              <td style={{padding:'10px',fontSize:11,fontWeight:800,color:'#fff',fontFamily:'sans-serif'}}>TOTAL</td>
              <td style={{padding:'10px',fontSize:14,fontWeight:800,color:'#FC8F8F',fontFamily:'sans-serif',textAlign:'right'}}>{fmtP(totalCur)}</td>
              <td style={{padding:'10px',fontSize:14,fontWeight:800,color:'#86EFAC',fontFamily:'sans-serif',textAlign:'right'}}>{fmtP(totalCur-savings)}</td>
              <td style={{padding:'10px',fontSize:15,fontWeight:900,color:'#86EFAC',fontFamily:'sans-serif',textAlign:'right'}}>{fmtP(savings)}</td>
              <td style={{padding:'10px',fontSize:12,fontWeight:700,color:'#86EFAC',fontFamily:'sans-serif',textAlign:'right'}}>{savPct}%</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div style={{padding:'10px 14px',background:'#F0F7FF',borderRadius:8,border:`1px solid ${BLUE}25`,marginBottom:14}}>
          <div style={{fontSize:8,color:'#475569',lineHeight:1.65}}>
            <strong>Methodology:</strong> Current premium estimates are based on NCCI/ISO published base rates calibrated to your industry, state, payroll, revenue, fleet size, and claims history. "Entered" figures reflect actual current premiums you provided. AOR target reflects achievable market rates based on competitive re-marketing across 40+ carriers. Estimates are not a guarantee of coverage or premium — individual results depend on underwriting approval and market conditions at time of binding.
          </div>
        </div>

        {/* Financial impact summary */}
        <div style={{background:'#F0FDF4',borderRadius:10,padding:'12px 16px',border:'1px solid #BBF7D0'}}>
          <div style={{fontSize:9,fontWeight:700,color:'#475569',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>5-Year Financial Impact Projection</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            {[
              {lb:'Year 1 Premium Savings',vl:fmtP(savings)},
              {lb:'5-Year Cost Advantage*',vl:fmtP(savings*5*1.03)},
              {lb:'Peer Average (Similar Size)',vl:fmtP(results.avgSavingsForSize)},
            ].map((c,i)=>(
              <div key={i} style={{textAlign:'center'}}>
                <div style={{fontSize:14,fontWeight:800,color:GREEN}}>{c.vl}</div>
                <div style={{fontSize:8,color:'#64748b'}}>{c.lb}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:7,color:'#94a3b8',marginTop:8}}>*5-Year projection assumes 3% annual premium growth. Reinvestment returns not included.</div>
        </div>

        <PFooter page={3} company={company}/>
      </div>

      {/* ── PAGE 4: COVERAGE GAPS ── */}
      {results.gaps.length>0&&(
        <div className="print-page" style={PAGE}>
          <PHeader title="Coverage Gap Analysis" subtitle={`${results.gaps.length} coverage insight${results.gaps.length!==1?'s':''} identified for ${company}`}/>
          <GapBlock gaps={criticalGaps} color={RED} label="CRITICAL — Required Coverage Missing"/>
          <GapBlock gaps={gapList} color={AMBER} label="COVERAGE GAP — Dangerous Exclusion"/>
          <GapBlock gaps={optList} color={BLUE} label="OPTIMIZATION — Savings Opportunity"/>
          <PFooter page={4} company={company}/>
        </div>
      )}

      {/* ── FINAL PAGE: NEXT STEPS & CONTACT ── */}
      <div className="print-page" style={{...PAGE,pageBreakAfter:'auto'}}>
        <PHeader title="Next Steps" subtitle="Ready to capture these savings? Here's how to proceed."/>

        <div style={{marginBottom:20}}>
          {[
            {n:'1',title:'Schedule a 15-Minute Review Call',desc:"Book a free call with an AOR specialist who knows your industry. We'll walk through this report line by line and confirm the savings opportunity specific to your account."},
            {n:'2',title:'Provide Access to Current Policies',desc:"Share your current declarations pages (or carrier login) so we can perform a full coverage audit. We handle everything — no extra work for your team."},
            {n:'3',title:'Receive Competitive Market Quotes',desc:"We'll approach the market simultaneously across 40+ carriers and present side-by-side comparisons with full coverage and terms analysis — not just the lowest price."},
            {n:'4',title:'Bind Optimized Coverage',desc:"When you approve, we handle the transition seamlessly — coordinating existing policy cancellation, certificate issuance, and scheduling your first renewal review."},
          ].map((s,i)=>(
            <div key={i} style={{display:'flex',gap:12,marginBottom:10,padding:'10px 12px',background:'#F8FAFC',borderRadius:10,border:'1px solid #E2E8F0'}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:BLUE,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:900,flexShrink:0}}>{s.n}</div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:DARK,marginBottom:2}}>{s.title}</div>
                <div style={{fontSize:9,color:'#475569',lineHeight:1.6}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{background:DARK,borderRadius:12,padding:'18px 22px',color:'#fff'}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
            <AORLogo height={30}/>
            <div>
              <div style={{fontSize:13,fontWeight:800}}>AOR Insurance</div>
              <div style={{fontSize:9,color:'rgba(255,255,255,0.5)'}}>A division of Sihle Insurance Group — Top 100 Independent Agency</div>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {[
              {lb:'Phone',vl:'(352) 901-1853'},
              {lb:'Email',vl:'branden@aorinsurance.com'},
              {lb:'Website',vl:'aorinsurance.com'},
              {lb:'Book a Meeting',vl:'aorinsurance.com/book'},
              {lb:'Licensed',vl:'All 50 States'},
              {lb:'Carriers',vl:'40+ A-Rated Carriers'},
            ].map((c,i)=>(
              <div key={i}>
                <div style={{fontSize:7,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:1}}>{c.lb}</div>
                <div style={{fontSize:10,fontWeight:600,color:'rgba(255,255,255,0.85)'}}>{c.vl}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginTop:14,fontSize:8,color:'#94a3b8',lineHeight:1.7,textAlign:'center'}}>
          This report was prepared by AOR Insurance based on information provided by the prospect. Premium estimates are indicative only and do not constitute a binding quote or professional insurance advice. Contact your AOR specialist for a formal coverage review and competitive market quotes.
        </div>

        <PFooter page={results.gaps.length>0?5:4} company={company}/>
      </div>
    </div>
  );
});

export default PrintReport;
